import { useEffect, useMemo, useRef, useState } from 'react';

type Option<T> = { value: T; label: string };

type Props<T> = {
  options: Option<T>[];
  value: T | null | undefined;
  onChange: (value: T) => void;
  placeholder?: string;
  disabled?: boolean;
};

export function SearchSelect<T extends string | number>({ options, value, onChange, placeholder, disabled }: Props<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const current = useMemo(() => options.find(o => o.value === value) || null, [options, value]);
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [open]);

  return (
    <div ref={containerRef} style={{position:'relative'}}>
      <div className="select-trigger" onClick={() => !disabled && setOpen(o=>!o)}>
        {current ? current.label : (placeholder || 'Выбрать...')}
      </div>
      {open && (
        <div 
          className="select-panel"
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: position.width,
            zIndex: 1000
          }}
        >
          <div className="searchbox">
            <input autoFocus placeholder="Поиск..." value={query} onChange={e=>setQuery(e.target.value)} style={{width:'100%', padding:'8px 10px', borderRadius:6, border:'1px solid var(--input-border)', background:'var(--input-bg)', color:'var(--text)'}} />
          </div>
          <div>
            {filtered.map(opt => (
              <div key={String(opt.value)} className="option" onClick={()=>{ onChange(opt.value); setOpen(false); setQuery(''); }}>
                {opt.label}
              </div>
            ))}
            {filtered.length === 0 && <div className="option" style={{color:'var(--muted)'}}>Ничего не найдено</div>}
          </div>
        </div>
      )}
    </div>
  );
}


